import React, { useState, useCallback } from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { useToast } from 'js/design_system/toasts/hooks';
import {
  PaperClaimingOnsiteModal,
  PaperToClaim,
} from './PaperClaimingOnsiteModal';
import { renderPaperClaimedToast } from 'js/mentions/KiiMatching/PaperClaimedToast';
import { recordShow, recordDismissal } from 'js/utils/dismissibles';

const NAG_NAME = 'PaperClaimingHomeProfileModal';

export type PaperClaimingHomeProfileInterruptionData = {
  papers: Array<{
    id: string; // OpenAlex Work ID (e.g., "https://openalex.org/W123")
    title: string;
    authorName: string; // camelized from backend's author_name
    openalexAuthorId: string; // OpenAlex Author ID (e.g., "https://openalex.org/A456")
  }>;
  variant: string;
  testName: string;
  surface: string; // 'home' or 'profile', set by backend based on page_type
};

type Props = {
  data: PaperClaimingHomeProfileInterruptionData;
  onComplete: () => void;
};

export const PaperClaimingHomeProfileInterruption: React.FC<Props> = ({
  data,
  onComplete,
}) => {
  const [open, setOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Record show on mount
  React.useEffect(() => {
    recordShow(NAG_NAME);
  }, []);

  // Transform backend data to match PaperClaimingModal props
  const papers: PaperToClaim[] = (data.papers || []).map((paper) => ({
    id: paper.id,
    title: paper.title,
    authorName: paper.authorName,
    openalexAuthorId: paper.openalexAuthorId,
  }));

  // Surface is set by the backend based on which page the user is on.
  const surface = data.surface || 'unknown';

  const handleClaimPapers = useCallback(
    async (paperIds: (number | string)[]) => {
      setIsLoading(true);
      let claimSucceeded = false;

      // Find the full paper objects for the selected IDs
      const selectedPapers = papers.filter((p) => paperIds.includes(p.id));

      try {
        const response = await fetchWithCsrf(
          routes.claim_api_v0_paper_claiming_home_profile_index_path({
            subdomain_param: 'api',
          }),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              papers: selectedPapers,
              surface,
              papers_shown: papers.length,
            }),
          },
        );
        claimSucceeded = response.ok;
      } catch {
        // Don't show error - modal will reappear on next visit if claim didn't persist
      } finally {
        setIsLoading(false);
      }

      setOpen(false);

      if (claimSucceeded && paperIds.length > 0) {
        toast({
          render: renderPaperClaimedToast(paperIds.length),
          placement: 'bottom-end',
          closeable: true,
        });
      }

      onComplete();
    },
    [papers, surface, onComplete, toast],
  );

  const handleDidNotAuthor = useCallback(async () => {
    // Count as dismissal since user definitively rejected these papers
    recordDismissal(NAG_NAME);

    try {
      await fetchWithCsrf(
        routes.none_relevant_api_v0_paper_claiming_home_profile_index_path({
          subdomain_param: 'api',
        }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            surface,
            papers_shown: papers.length,
          }),
        },
      );
    } catch {
      // Best-effort tracking
    }

    setOpen(false);
    onComplete();
  }, [papers.length, surface, onComplete]);

  const handleClose = useCallback(async () => {
    recordDismissal(NAG_NAME);

    try {
      await fetchWithCsrf(
        routes.dismiss_api_v0_paper_claiming_home_profile_index_path({
          subdomain_param: 'api',
        }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            surface,
            variant: data.variant,
          }),
        },
      );
    } catch {
      // Best-effort dismiss tracking
    }

    setOpen(false);
    onComplete();
  }, [data.variant, surface, onComplete]);

  return (
    <PaperClaimingOnsiteModal
      open={open}
      onClose={handleClose}
      papers={papers}
      onClaimPapers={handleClaimPapers}
      onDidNotAuthor={handleDidNotAuthor}
      isLoading={isLoading}
    />
  );
};

export default PaperClaimingHomeProfileInterruption;
